import { catchError, debounceTime, distinctUntilChanged, map, Observable, of, switchMap } from 'rxjs';
import { ChargingStationLocationService } from 'src/app/shared/services/charging-station-location.service';

export interface AddressEligibilityInput {
  street?: string | null;
  houseNumber?: string | null;
  zipCode?: string | null;
}

export interface DynamicPricePolicyEligibility {
  addressKey: string | null;
  canHaveDynamicPricePolicy: boolean;
}

// Identifies the address parts the net area depends on; null for an incomplete address.
export function netAreaAddressKey({ street, houseNumber, zipCode }: AddressEligibilityInput): string | null {
  const road = street?.trim().toLowerCase();
  const number = Number.parseInt(`${houseNumber ?? ''}`, 10);
  const postalCode = zipCode?.trim();
  return road && postalCode && !Number.isNaN(number) ? `${road}|${number}|${postalCode}` : null;
}

// Emits whether the address can have a dynamic price policy. Incomplete addresses and failed lookups count as not eligible.
export function dynamicPricePolicyEligibility(
  address$: Observable<AddressEligibilityInput>,
  locationService: ChargingStationLocationService
): Observable<DynamicPricePolicyEligibility> {
  return address$.pipe(
    debounceTime(600),
    map(address => ({ address, addressKey: netAreaAddressKey(address) })),
    distinctUntilChanged((a, b) => a.addressKey === b.addressKey),
    switchMap(({ address, addressKey }) => {
      if (addressKey === null) {
        return of({ addressKey, canHaveDynamicPricePolicy: false });
      }
      return locationService
        .getDynamicPricePolicyEligibility(address.street!.trim(), Number.parseInt(`${address.houseNumber}`, 10), address.zipCode!.trim())
        .pipe(
          map(response => ({ addressKey, canHaveDynamicPricePolicy: response.can_have_dynamic_price_policy })),
          catchError(() => of({ addressKey, canHaveDynamicPricePolicy: false }))
        );
    })
  );
}
